/**
 * WalletPassPreview — the pass FRONT as Wallet lays it out, rendered from the
 * registration's own upload fields (2026-08-10, PM-requested).
 *
 * Why front-only: the front is where every uploaded asset and configured field
 * actually lands (icon · logo · strip · logoText · primary/secondary/auxiliary
 * fields · barcode), so it is the only view that can tell an operator whether
 * what they uploaded will look right. The back is a label/value list — it
 * validates nothing visual.
 *
 * HONESTY RULES this component holds to:
 *  - Image slots render the REAL asset wherever one exists: the file uploaded
 *    in THIS session (`walletAssetStore` — the freshest truth, it always wins)
 *    or the stored copy read back via `getIntegrationFeatureCredentialImage`
 *    (JAI-550; serves image-asset fields only, never certificates). A slot
 *    with nothing stored keeps its dashed "still needed" state — 404 is the
 *    normal empty answer, not an error — and a configured image that fails to
 *    load says so rather than substituting a stand-in.
 *  - Field VALUES are sample data, pilled as such. Pass field mapping is not a
 *    configurable surface on this registration (the record carries the
 *    certificate and images only), so the layout shown is the fixed default for
 *    each pass style.
 *  - The barcode is a placeholder block, never a rendered-scannable code.
 *
 * ui-config — presentational only; reads the registration record it is given.
 */
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Card, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { ApiError, getIntegrationFeatureCredentialImage } from "@v0y4ge/api-client";
import { useT } from "../../lib/i18n";
import { PASS_STYLE_DEFAULTS, passContrast, readPassAppearance, resolvePassColors } from "./integration-configs";
import type { RegDetail, RegFeature } from "./AppRegistration";

type Bag = Record<string, unknown>;

/** Read-back state for one slot's stored image. `absent` (404) is the normal
 * empty state; `error` is a genuine failure and is stated, never faked. */
interface StoredImgState { status: "loading" | "loaded" | "absent" | "error"; dataUri?: string }

/* ─── Session-only asset registry ─────────────────────────────────────────────
 * Object URLs for files uploaded in this session, keyed
 * `${provider}:${featureKey}:${fieldKey}`. Deliberately in-memory: a reload
 * clears it, which is correct — nothing here is persisted state, it is the file
 * the operator just handed the browser.
 *
 * Bounded (2026-09-12, memory pass): the registry only ever revoked the
 * PREVIOUS URL for the same slot, so every distinct provider/feature/field
 * combination an operator touched kept its full-resolution image for the whole
 * session — a wallet registration has ~10 image slots and a session can walk
 * several registrations. It now evicts least-recently-written entries past
 * MAX_ASSETS, and exposes explicit removal for a cleared field. */
const MAX_ASSETS = 12;
const assets = new Map<string, string>();
const subs = new Set<() => void>();
/** Drop one entry and revoke its URL. Returns true when something was held. */
function dropAsset(k: string): boolean {
  const prev = assets.get(k);
  if (!prev) return false;
  URL.revokeObjectURL(prev);
  assets.delete(k);
  return true;
}
export const walletAssetStore = {
  key: (provider: string, featureKey: string, fieldKey: string) => `${provider}:${featureKey}:${fieldKey}`,
  put(provider: string, featureKey: string, fieldKey: string, file: File) {
    if (!/^image\//.test(file.type)) return;
    const k = this.key(provider, featureKey, fieldKey);
    dropAsset(k);
    assets.set(k, URL.createObjectURL(file));
    // Map iteration is insertion order, so the first key is the oldest write.
    while (assets.size > MAX_ASSETS) {
      const oldest = assets.keys().next();
      if (oldest.done) break;
      dropAsset(oldest.value);
    }
    subs.forEach((fn) => fn());
  },
  get(provider: string, featureKey: string, fieldKey: string): string | undefined {
    return assets.get(this.key(provider, featureKey, fieldKey));
  },
  /** Forget one slot's upload — for a field the operator cleared. */
  remove(provider: string, featureKey: string, fieldKey: string) {
    if (dropAsset(this.key(provider, featureKey, fieldKey))) subs.forEach((fn) => fn());
  },
  /** Forget everything (leaving a registration, signing out). */
  clear() {
    if (!assets.size) return;
    Array.from(assets.keys()).forEach(dropAsset);
    subs.forEach((fn) => fn());
  },
  subscribe(fn: () => void) { subs.add(fn); return () => { subs.delete(fn); }; },
};

function useAssetVersion(): number {
  const [v, setV] = useState(0);
  useEffect(() => walletAssetStore.subscribe(() => setV((n) => n + 1)), []);
  return v;
}

/* ─── Slot classification ────────────────────────────────────────────────────
 * Slots are resolved from each upload field's own key/label + dimensions rather
 * than a hardcoded key list, so a backend rename or a new image field does not
 * silently drop out of the preview. */
type SlotKind = "icon" | "logo" | "strip" | "thumbnail" | "background";
interface Slot { kind: SlotKind; fieldKey: string; label: string; w?: number; h?: number; configured: boolean; scale: number }

const isCert = (f: Bag) => String(f.type ?? "") === "signing-certificate";
const uploadBlock = (f: Bag): Bag | null => {
  const u = f.upload as Bag | undefined;
  return u && typeof u === "object" ? u : null;
};
function classify(f: Bag): SlotKind | null {
  const s = `${String(f.key ?? "")} ${String(f.label ?? "")}`.toLowerCase();
  if (/strip/.test(s)) return "strip";
  if (/thumb/.test(s)) return "thumbnail";
  if (/background/.test(s)) return "background";
  if (/logo/.test(s)) return "logo";
  if (/icon/.test(s)) return "icon";
  return null;
}
/** @2x/@3x variants describe the same slot — the preview shows the 1x one. */
function scaleOf(f: Bag): number {
  const s = `${String(f.key ?? "")} ${String(f.label ?? "")}`;
  const m = /@\s*([23])\s*x/i.exec(s);
  return m ? Number(m[1]) : 1;
}

function walletFeature(detail: RegDetail): RegFeature | null {
  const feats = Array.isArray(detail.features) ? detail.features : [];
  return feats.find((f) => (f.configFields ?? []).some((cf) => {
    const u = uploadBlock(cf as Bag);
    return !!u && !isCert(cf as Bag) && !!classify(cf as Bag);
  })) ?? null;
}

function slotsOf(feature: RegFeature): Slot[] {
  const out: Slot[] = [];
  (feature.configFields ?? []).forEach((raw) => {
    const f = raw as Bag;
    const u = uploadBlock(f);
    if (!u || isCert(f)) return;
    const kind = classify(f);
    if (!kind) return;
    out.push({
      kind,
      fieldKey: String(f.key ?? ""),
      label: String(f.label ?? f.key ?? ""),
      w: typeof u.exactWidth === "number" ? u.exactWidth : (typeof u.maxWidth === "number" ? u.maxWidth : undefined),
      h: typeof u.exactHeight === "number" ? u.exactHeight : (typeof u.maxHeight === "number" ? u.maxHeight : undefined),
      configured: !!f.configured || !!f.credential,
      scale: scaleOf(f),
    });
  });
  // One entry per slot kind: prefer the 1x field, which carries the base dimensions.
  const best = new Map<SlotKind, Slot>();
  out.forEach((s) => {
    const cur = best.get(s.kind);
    if (!cur || s.scale < cur.scale) best.set(s.kind, s);
  });
  return Array.from(best.values());
}

/* ─── Pass styles ───────────────────────────────────────────────────────────
 * The three things this platform issues. eventTicket carries the strip; the
 * appointment and queue passes are `generic` (no strip, optional thumbnail) —
 * which is exactly why the switcher matters: the same uploads land differently. */
type StyleId = "eventTicket" | "appointment" | "queue";
interface PassStyle {
  id: StyleId; tab: string; passStyle: string; strip: boolean; thumbnail: boolean;
  header: { l: string; v: string };
  primary: { l: string; v: string };
  secondary: { l: string; v: string }[];
  aux: { l: string; v: string }[];
  bg: string; fg: string; label: string;
  /* JAI-581 — resolved label colour (configured value, or the historical
   * fg-at-0.62 fallback already composited over bg). */
  labelColor: string;
}

/* ─── Slot rendering ────────────────────────────────────────────────────────── */
function SlotImg({ provider, featureKey, slot, stored, width, height, radius = 4, fit = "contain", missingLabel }: {
  provider: string; featureKey: string; slot: Slot | undefined; stored?: StoredImgState;
  width: number | string; height: number; radius?: number; fit?: "contain" | "cover"; missingLabel?: string;
}) {
  const t = useT();
  useAssetVersion();
  const sessionSrc = slot ? walletAssetStore.get(provider, featureKey, slot.fieldKey) : undefined;
  const dims = slot?.w && slot?.h ? `${slot.w}\u00d7${slot.h}` : "";
  // Precedence: the file the operator just chose wins over the stored copy —
  // the two can never disagree on screen.
  const src = sessionSrc ?? (stored?.status === "loaded" ? stored.dataUri : undefined);
  if (src) {
    return <img src={src} alt="" aria-hidden="true" title={slot && dims ? `${slot.label} \u00b7 ${dims}` : undefined}
      width={typeof width === "number" ? width : undefined} height={height}
      style={{ width, height, objectFit: fit, borderRadius: radius, display: "block", background: "#fff" }} />;
  }
  const configured = !!slot?.configured;
  const loading = configured && stored?.status === "loading";
  const failed = configured && stored?.status === "error";
  // The box reserves the slot's known size in every state, so the card never
  // reflows as stored images arrive.
  return (
    <div
      style={{
        width, height, borderRadius: radius, display: "flex", alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: 4, boxSizing: "border-box",
        border: `1px dashed ${failed ? "#C9A0A0" : configured ? "#9AA6B2" : "#C7CDD4"}`,
        background: failed ? "rgba(208,22,71,0.05)" : configured ? "rgba(46,110,142,0.06)" : "rgba(17,24,39,0.03)",
        color: failed ? "#8A4A55" : configured ? "#4A5A68" : "#8A939C", fontSize: 9, lineHeight: 1.25, overflow: "hidden",
      }}
    >
      {failed ? (
        <span>{t("settings.walletPreview.imageUnavailable", "This image is configured but couldn\u0027t be loaded.")}</span>
      ) : loading ? (
        <span aria-hidden="true">{dims || (slot?.label ?? "")}</span>
      ) : (
        <span>
          {missingLabel ?? slot?.label ?? t("settings.walletPreview.image", "Image")}
          {dims ? <><br />{dims}</> : null}
          {configured ? <><br />{t("settings.walletPreview.uploadedShort", "uploaded")}</> : null}
        </span>
      )}
    </div>
  );
}

function FieldBlock({ l, v, fg, labelColor, size = 13, align = "left" }: { l: string; v: string; fg: string; labelColor?: string; size?: number; align?: "left" | "right" }) {
  // Overflow-proof by construction: label and value ellipsize inside their flex
  // track (minWidth: 0) and carry the full text in `title`, so no string, in any
  // locale, at any length, can widen or break the 264px pass front.
  return (
    <div style={{ minWidth: 0, flexShrink: 1, overflow: "hidden", textAlign: align }}>
      {/* JAI-581: a configured pass_label_color paints at full opacity; unset
          keeps the historical fg-at-0.62 fallback. The contrast advisory
          measures the composited result of whichever branch runs here. */}
      <div title={l} style={{ fontSize: 8.5, letterSpacing: 0.6, textTransform: "uppercase", color: labelColor ?? fg, opacity: labelColor ? 1 : 0.62, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l}</div>
      <div title={v} style={{ fontSize: size, color: fg, fontWeight: 500, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</div>
    </div>
  );
}

/* ─── The card ─────────────────────────────────────────────────────────────── */
export function WalletPassPreview({ detail }: { detail: RegDetail }) {
  const t = useT();
  const feature = walletFeature(detail);
  const slots = useMemo(() => (feature ? slotsOf(feature) : []), [feature]);
  const [styleId, setStyleId] = useState<StyleId>("eventTicket");
  useAssetVersion(); // header's logo-vs-logoText choice reads the store directly
  const [storedImgs, setStoredImgs] = useState<Record<string, StoredImgState>>({});
  useEffect(() => {
    if (!feature) return;
    const prov = String(detail.provider);
    const fk = String(feature.key);
    // Driven by the slot list, which already excludes certificates (isCert in
    // slotsOf) — so FIELD_NOT_READABLE is unreachable by construction. A slot
    // whose file was uploaded this session keeps the session URL instead.
    const pending = slots.filter((sl) => sl.configured && !storedImgs[sl.fieldKey] && !walletAssetStore.get(prov, fk, sl.fieldKey));
    if (pending.length === 0) return;
    let cancelled = false;
    setStoredImgs((m) => { const next = { ...m }; pending.forEach((sl) => { next[sl.fieldKey] = { status: "loading" }; }); return next; });
    pending.forEach((sl) => {
      getIntegrationFeatureCredentialImage(prov, fk, sl.fieldKey)
        .then((res) => { if (!cancelled) setStoredImgs((m) => ({ ...m, [sl.fieldKey]: { status: "loaded", dataUri: res.data.dataUri } })); })
        .catch((e: unknown) => {
          const absent = e instanceof ApiError && e.code === "NOT_FOUND"; // nothing stored — normal, not an error
          if (!cancelled) setStoredImgs((m) => ({ ...m, [sl.fieldKey]: { status: absent ? "absent" : "error" } }));
        });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps — storedImgs is a fetch-once guard, not an input
  }, [detail.provider, feature, slots]);
  if (!feature || slots.length === 0) return null;

  const provider = String(detail.provider);
  const featureKey = String(feature.key);
  const byKind = (k: SlotKind) => slots.find((s) => s.kind === k);
  const bgSlot = byKind("background");
  const bgStored = storedImgs[bgSlot?.fieldKey ?? ""];
  const bgSrc = (bgSlot ? walletAssetStore.get(provider, featureKey, bgSlot.fieldKey) : undefined)
    ?? (bgStored?.status === "loaded" ? bgStored.dataUri : undefined);
  const logoText = t("settings.walletPreview.sampleOrgName", "Your organisation");
  /* JAI-581 — tenant-default appearance. ONE colour set applies to all three
   * styles, so a configured background replaces the per-style default on every
   * tab and the card stops changing as you switch. That reads as correct only
   * because the tabs are labelled: per-style colours and tab labelling have to
   * move together, in either direction. The per-style values below are now the
   * UNSET case, not a fixed identity. */
  const appearance = readPassAppearance(feature.configFields);
  const colorsFor = (id: string) => {
    const def = PASS_STYLE_DEFAULTS.find((d) => d.id === id) ?? PASS_STYLE_DEFAULTS[0];
    return resolvePassColors(def, appearance);
  };

  const STYLES: PassStyle[] = [
    {
      id: "eventTicket", tab: t("settings.walletPreview.tabEventTicket", "Event ticket"), passStyle: "eventTicket",
      strip: true, thumbnail: false, bg: colorsFor("eventTicket").bg, fg: colorsFor("eventTicket").fg, labelColor: colorsFor("eventTicket").label,
      label: t("settings.walletPreview.eventTicketNote", "Strip image sits behind the primary field \u2014 the one asset that changes the whole front."),
      header: { l: t("settings.walletPreview.doors", "Doors"), v: "6:30 PM" },
      primary: { l: t("settings.walletPreview.event", "Event"), v: "Autumn Investor Briefing" },
      secondary: [
        { l: t("settings.walletPreview.guest", "Guest"), v: "Sample Guest" },
        { l: t("settings.walletPreview.seat", "Seat"), v: "G-14" },
      ],
      aux: [
        { l: t("settings.walletPreview.when", "When"), v: "Sat 12 Sep \u00b7 7:00 PM" },
        { l: t("settings.walletPreview.where", "Where"), v: "Lakeview Hall" },
      ],
    },
    {
      id: "appointment", tab: t("settings.walletPreview.tabAppointment", "Appointment"), passStyle: "generic",
      strip: false, thumbnail: true, bg: colorsFor("appointment").bg, fg: colorsFor("appointment").fg, labelColor: colorsFor("appointment").label,
      label: t("settings.walletPreview.appointmentNote", "Generic style \u2014 no strip; the thumbnail, if uploaded, sits beside the primary field."),
      header: { l: t("settings.walletPreview.time", "Time"), v: "10:30" },
      primary: { l: t("settings.walletPreview.appointment", "Appointment"), v: "Mortgage review" },
      secondary: [
        { l: t("settings.walletPreview.with", "With"), v: "Sample Adviser" },
        { l: t("settings.walletPreview.duration", "Duration"), v: "45 min" },
      ],
      aux: [
        { l: t("settings.walletPreview.when", "When"), v: "Tue 12 Aug \u00b7 10:30" },
        { l: t("settings.walletPreview.where", "Where"), v: "Lakeview branch" },
      ],
    },
    {
      id: "queue", tab: t("settings.walletPreview.tabQueue", "Queue position"), passStyle: "generic",
      strip: false, thumbnail: false, bg: colorsFor("queue").bg, fg: colorsFor("queue").fg, labelColor: colorsFor("queue").label,
      label: t("settings.walletPreview.queueNote", "Generic style, updated in place \u2014 position and wait change on the pass already in the wallet."),
      header: { l: t("settings.walletPreview.ahead", "Ahead"), v: "4" },
      primary: { l: t("settings.walletPreview.position", "Position"), v: "A-27" },
      secondary: [
        { l: t("settings.walletPreview.queue", "Queue"), v: "Registrar" },
        { l: t("settings.walletPreview.estWait", "Est. wait"), v: "12 min" },
      ],
      aux: [
        { l: t("settings.walletPreview.joined", "Joined"), v: "10:12" },
        { l: t("settings.walletPreview.where", "Where"), v: "Lakeview branch" },
      ],
    },
  ];
  const st = STYLES.find((s) => s.id === styleId) ?? STYLES[0];
  /* Was `st.fg === "#FFFFFF"`. With a tenant colour that literal is wrong on
   * every configured pass, so decide from the resolved background's luminance. */
  const dark = passContrast(st.bg, "#FFFFFF") > passContrast(st.bg, "#101828");
  const divider = dark ? "rgba(255,255,255,0.18)" : "rgba(16,24,40,0.10)";

  return (
    <Card style={{ padding: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={tx.label as CSSProperties}>{t("settings.walletPreview.title", "Pass preview")}</div>
        <Pill tone="neutral">{t("settings.walletPreview.sampleData", "Sample data")}</Pill>
        <span style={{ flex: 1 }} />
        <code style={{ fontFamily: T.fontMono, fontSize: 10, color: T.soft }}>{st.passStyle}</code>
      </div>

      <div style={{ padding: "10px 16px 0", display: "flex", gap: 6 }}>
        {STYLES.map((s) => {
          const on = s.id === styleId;
          return (
            // ui-raw: segmented pass-style switch — SegmentedControl's role.
            <button key={s.id} type="button" title={s.tab} onClick={() => setStyleId(s.id)}
              style={{
                flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "5px 6px", fontSize: 11, fontWeight: on ? 600 : 500, cursor: "pointer",
                borderRadius: 6, border: `1px solid ${on ? T.p1 : T.border}`,
                background: on ? `color-mix(in srgb, ${T.p1} 8%, ${T.white})` : T.white,
                color: on ? T.p1 : T.n2, fontFamily: "inherit",
              }}>
              {s.tab}
            </button>
          );
        })}
      </div>

      {/* Pass front — Wallet's own front geometry: header row, strip (event
        * tickets only), primary field, secondary/auxiliary rows, barcode. */}
      <div style={{ padding: "14px 16px 4px", display: "flex", justifyContent: "center", background: T.n6, margin: "12px 0 0", borderTop: `1px solid ${T.border}` }}>
        {/* JAI-581 — pass_background_image (180×220 PNG, image-asset) paints
          * behind the card face when one is stored. Rendered ONLY when a real
          * asset resolves: an unconfigured background must leave the colour
          * showing, never a dashed placeholder covering the whole front. */}
        <div style={{ position: "relative", width: 264, borderRadius: 14, background: st.bg, color: st.fg, boxShadow: "0 8px 24px rgba(16,24,40,0.18), 0 1px 2px rgba(16,24,40,0.14)", overflow: "hidden", border: dark ? "none" : `1px solid ${T.border}` }}>
          {bgSrc ? <img src={bgSrc} alt="" aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} /> : null}
          <div style={{ position: "relative" }}>
          {/* Header: icon · logo/logoText · one header field */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px 8px" }}>
            <SlotImg provider={provider} featureKey={featureKey} slot={byKind("icon")} stored={storedImgs[byKind("icon")?.fieldKey ?? ""]} width={22} height={22} radius={5} />
            {walletAssetStore.get(provider, featureKey, byKind("logo")?.fieldKey ?? "") || byKind("logo")?.configured ? (
              <SlotImg provider={provider} featureKey={featureKey} slot={byKind("logo")} stored={storedImgs[byKind("logo")?.fieldKey ?? ""]} width={72} height={20} radius={3} />
            ) : (
              <span title={logoText} style={{ fontSize: 12, fontWeight: 600, letterSpacing: -0.1, color: st.fg, opacity: 0.95, minWidth: 0, flexShrink: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{logoText}</span>
            )}
            <span style={{ flex: 1 }} />
            <FieldBlock l={st.header.l} v={st.header.v} fg={st.fg} labelColor={st.labelColor} size={12} align="right" />
          </div>

          {st.strip ? (
            <SlotImg provider={provider} featureKey={featureKey} slot={byKind("strip")} stored={storedImgs[byKind("strip")?.fieldKey ?? ""]} width="100%" height={87} radius={0} fit="cover"
              missingLabel={byKind("strip")?.label ?? t("settings.walletPreview.strip", "Strip")} />
          ) : null}

          <div style={{ padding: "12px 12px 10px", display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 8.5, letterSpacing: 0.6, textTransform: "uppercase", color: st.labelColor, fontWeight: 600 }}>{st.primary.l}</div>
                <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: -0.3, lineHeight: 1.2, marginTop: 2 }}>{st.primary.v}</div>
              </div>
              {st.thumbnail && byKind("thumbnail") ? (
                <SlotImg provider={provider} featureKey={featureKey} slot={byKind("thumbnail")} stored={storedImgs[byKind("thumbnail")?.fieldKey ?? ""]} width={44} height={44} radius={6} fit="cover" />
              ) : null}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", borderTop: `1px solid ${divider}`, paddingTop: 8 }}>
              {st.secondary.map((f) => <FieldBlock key={f.l} l={f.l} v={f.v} fg={st.fg} labelColor={st.labelColor} />)}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
              {st.aux.map((f) => <FieldBlock key={f.l} l={f.l} v={f.v} fg={st.fg} labelColor={st.labelColor} size={11} />)}
            </div>
          </div>

          {/* Barcode — placeholder, never a scannable code. */}
          <div style={{ background: dark ? "rgba(255,255,255,0.94)" : "#fff", padding: "10px 12px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, borderTop: `1px solid ${divider}` }}>
            <div style={{
              width: 66, height: 66, borderRadius: 4, border: "1px dashed #B7BEC6",
              display: "flex", alignItems: "center", justifyContent: "center",
              backgroundImage: "linear-gradient(45deg, rgba(16,24,40,0.07) 25%, transparent 25%, transparent 75%, rgba(16,24,40,0.07) 75%), linear-gradient(45deg, rgba(16,24,40,0.07) 25%, transparent 25%, transparent 75%, rgba(16,24,40,0.07) 75%)",
              backgroundSize: "10px 10px", backgroundPosition: "0 0, 5px 5px",
            }}>
              <span style={{ fontSize: 8.5, color: "#6B7480", background: "rgba(255,255,255,0.85)", padding: "1px 4px", borderRadius: 3 }}>
                {t("settings.walletPreview.barcode", "QR")}
              </span>
            </div>
            <span style={{ fontSize: 8.5, color: "#8A939C", fontFamily: T.fontMono }}>
              {t("settings.walletPreview.serialSample", "serial \u00b7 sample")}
            </span>
          </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "10px 16px 14px", display: "flex", flexDirection: "column", gap: 6, background: T.n6, borderBottom: `1px solid ${T.border}` }}>
        <p style={{ fontSize: 11, color: T.n2, lineHeight: 1.5, margin: 0 }}>{st.label}</p>
      </div>

      {/* What the preview can and cannot show — stated, not implied. */}
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
        <p style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55, margin: 0 }}>
          {t("settings.walletPreview.assetsHint", "Dashed slots are images this pass still needs \u2014 each names its required size. Images already uploaded are shown as they will appear in Wallet.")}
        </p>
        <p style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.55, margin: 0 }}>
          {t("settings.walletPreview.fieldsNote", "Field values are sample data on the default layout for each pass style \u2014 pass field mapping is not configurable on this registration.")}
        </p>
      </div>
    </Card>
  );
}
